/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, Inject, signal} from '@angular/core';
import {resourceOptionParams} from '../../common/controller/signal-admin-resource-api';
import * as _ from 'lodash';
import {AngularModalService} from '../../common/modal/angular-modal.service';
import template from './product-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {ADN_TARGETING_TYPES} from '../../targeting/targeting-constants';
import {LayoutSelectorModalComponent} from './layout-selector-modal.component';
import {TargetingEditorModalService} from '../../targeting/targeting-editor-modal.service';
import {OrderApiService} from '../../order/order-api.service';
import {LayoutApiService} from '../layout/layout-api.service';
import {ReportTemplateApiService} from '../../report-template/report-template-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {ProductApiService, ProductModel} from './product-api.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {UIRouterModule} from '@uirouter/angular';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {APP_SHARED} from '../../common/app-shared';
import {ADMIN_RESOURCE_PAGES} from '../../common/controller/admin-resource-pages';
import {ADMIN_LIST} from '../../common/directives/admin-list';
import {FORM_CONTROLS} from '../../common/directives/form-controls/form-controls';
import {LayoutComponentSummaryComponent} from '../../common/layout-component-summary-component';
import {TARGETING_COMPONENTS} from '../../targeting/targeting-components';


@Component({
  standalone: true,
  imports: [
    ...APP_SHARED,
    FormsModule,
    TranslateModule,
    UIRouterModule,
    ButtonsModule,
    ...ADMIN_LIST,
    ...ADMIN_RESOURCE_PAGES,
    ...FORM_CONTROLS,
    LayoutComponentSummaryComponent,
    ...TARGETING_COMPONENTS
  ],
  selector: 'adn-product-edit-page',
  template
})
export class ProductEditPageComponent extends SignalAdminResourceEditPageBase<ProductModel> {
  private readonly hasLayoutsState = signal(false);
  private readonly suiOrdersState = signal<any[] | null>(null);
  private readonly reportTemplatesState = signal<any[] | null>(null);
  private readonly targetingResultState = signal<any>({});
  private readonly creativeTargetingResultState = signal<any>({});
  private readonly layoutResultState = signal<any>({});

  get hasLayouts(): boolean { return this.hasLayoutsState(); }
  set hasLayouts(value: boolean) { this.hasLayoutsState.set(value); }
  get suiOrders(): any[] | null { return this.suiOrdersState(); }
  set suiOrders(value: any[] | null) { this.suiOrdersState.set(value); }
  get reportTemplates(): any[] | null { return this.reportTemplatesState(); }
  set reportTemplates(value: any[] | null) { this.reportTemplatesState.set(value); }
  get targetingResult(): any { return this.targetingResultState(); }
  set targetingResult(value: any) { this.targetingResultState.set(value); }
  get creativeTargetingResult(): any { return this.creativeTargetingResultState(); }
  set creativeTargetingResult(value: any) { this.creativeTargetingResultState.set(value); }
  get layoutResult(): any { return this.layoutResultState(); }
  set layoutResult(value: any) { this.layoutResultState.set(value); }
  isSelfService = false;
  tiers: any[] = [];

  constructor(
    @Inject(LocalNetworkProfile) private readonly productNetworkProfile: any,
    @Inject(LayoutApiService) private Layout: LayoutApiService,
    private readonly productApi: ProductApiService,
    private readonly orderApi: OrderApiService,
    private readonly reportTemplateApi: ReportTemplateApiService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    private angularModalService: AngularModalService,
    private targetingEditorModalService: TargetingEditorModalService
  ) {
    super(productApi, 'product');
    this.isSelfService = LocalUserPermissions.isSelfService();

    this.orderApi.listOptions({joins: 'inner join team on team.id = order.teamId', where: 'team.type in SUI'}).subscribe((page: any) => {
      deferSignalStateWrite(() => {
        this.suiOrders = page.results;
      });
    });

    this.reportTemplateApi.listOptions({filterBy: 'reportType', filterByValue: 'SELF_SERVICE'}).subscribe((page) => {
      deferSignalStateWrite(() => {
        this.reportTemplates = page.results;
      });
    });
  }

  protected override afterEditingModelReset(): void {
    this.hasLayouts = false;
    if (this.editingModel.layouts && this.editingModel.layouts.length) {
      firstValueFrom(this.Layout.listOptions(resourceOptionParams(_.map(this.editingModel.layouts, 'id')))).then((page: any) => {
        // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
        deferSignalStateWrite(() => {
          this.editingModel = {...this.editingModel, layouts: page.results};
          this.hasLayouts = true;
        });
      });
    } else {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this.hasLayouts = true;
      });
    }

    this.editingModel.layouts = this.editingModel.layouts || [];
    this.editingModel.cpmSpecification = this.editingModel.cpmSpecification || 'FIXED';

    this.editingModel.allowedDimensions = _.isArray(this.editingModel.allowedDimensions) ? this.editingModel.allowedDimensions : [[]];
    this.editingModel.allowedDimensions[0] = _.isArray(this.editingModel.allowedDimensions[0]) ? this.editingModel.allowedDimensions[0] : [];

    this.editingModel.data = this.editingModel.data || {};
    this.editingModel.data.presentationOptions = this.editingModel.data.presentationOptions || {};

    this.editingModel.targeting = this.editingModel.targeting || {};
    this.editingModel.creativeTargeting = this.editingModel.creativeTargeting || {};

    this.targetingResult = {};
    this.creativeTargetingResult = {};
    this.layoutResult = {};

    const enabledTiers = this.productNetworkProfile.get('tiers') || [];
    const currentTier = this.editingModel.defaultTier;
    this.tiers = _.cloneDeep(enabledTiers);
    if (currentTier) {
      const hasCurrent = !!_.find(this.tiers, (t: any) => t.id === _.get(currentTier, 'id'));
      if (!hasCurrent) {
        this.tiers.push(currentTier);
      }
    }
    _.forEach(this.tiers, (t: any) => {
      t.displayName = t.name + (_.find(enabledTiers, {id: t.id}) ? '' : ' (disabled)');
    });

    this.editingModel.defaultOrders = _.map(this.editingModel.defaultOrders, (o: any) => _.isString(o) ? o : o.id);
  }

  protected override afterSave(): void {
    this.targetingResult = {...this.targetingResult, data: false};
    this.creativeTargetingResult = {...this.creativeTargetingResult, data: false};
  }

  protected override prepareForSubmit(model: ProductModel): void {
    if (model.minimumCpm && !_.isFinite(model.minimumCpm.amount)) {
      model.minimumCpm = null;
    }
    if (model.minimumCpc && !_.isFinite(model.minimumCpc.amount)) {
      model.minimumCpc = null;
    }
  }

  addDimensions(): void {
    this.editingModel.allowedDimensions = this.editingModel.allowedDimensions || [];
    this.editingModel.allowedDimensions.push([]);
    this.markExternalFormDirty();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.editingModel.allowedDimensions, index);
    this.markExternalFormDirty();
  }

  isInDefaultOrders(orderId: string): boolean {
    return (this.editingModel.defaultOrders || []).indexOf(orderId) > -1;
  }

  toggleDefaultOrder(orderId: string): void {
    this.editingModel.defaultOrders = this.editingModel.defaultOrders || [];
    const idx = this.editingModel.defaultOrders.indexOf(orderId);
    if (idx > -1) {
      this.editingModel.defaultOrders.splice(idx, 1);
    } else {
      this.editingModel.defaultOrders.push(orderId);
    }
    this.markExternalFormDirty();
  }

  editTarget(targeting: any, isCreative = false): void {
    const typeTargeting = isCreative ? 'creativeTargetingResult' : 'targetingResult';
    const apiParam = isCreative ? 'creativeTargeting' : 'targeting';
    this[typeTargeting] = {data: false};

    this.targetingEditorModalService.open({
      modelLineItem: _.cloneDeep(this.editingModel),
      lineItemType: 'NORMAL',
      pageType: isCreative ? 'optionalProduct' : 'product',
      reachEnabled: undefined,
      mandatoryTargets: undefined,
      availableTargets: isCreative ? _.pick(ADN_TARGETING_TYPES, ['site', 'siteGroup', 'userSegment', 'category', 'namedLocation', 'device', 'keyValue', 'domain']) : null,
      targeting,
      overridableTargeting: undefined,
      targetingMode: null,
      isLockedDown: false
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      _.assign(targeting, wc.wc);
      this.editingModel[apiParam] = _.cloneDeep(targeting);
      this.model[apiParam] = _.cloneDeep(targeting);
      if (!this.isNew) {
        firstValueFrom(this.productApi.savePartial(this.editingModel, apiParam)).then((respObj: any) => {
          this[typeTargeting] = {...this[typeTargeting], data: true};
          this.editingModel = {...this.editingModel, validationWarnings: respObj.validationWarnings};
        });
      } else {
        this.markExternalFormDirty();
      }
    });
  }

  editLayouts(): void {
    this.layoutResult = {data: false};
    const modalRef = this.angularModalService.show(LayoutSelectorModalComponent, {
      class: 'modal-lg',
      initialState: {currentLayouts: _.cloneDeep(this.editingModel.layouts)}
    });
    (modalRef.content as LayoutSelectorModalComponent).onSaved.subscribe((layouts: any[]) => {
      this.editingModel = {...this.editingModel, layouts};
      this.markExternalFormDirty();
      if (!this.isNew) {
        firstValueFrom(this.productApi.savePartial(this.editingModel, 'layouts')).then((respObj: any) => {
          this.layoutResult = {...this.layoutResult, data: true};
          this.editingModel = {...this.editingModel, validationWarnings: respObj.validationWarnings};
        });
      }
    });
  }
}
